import { useEffect, useState } from "react";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Trophy } from "lucide-react";
import { api, money } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ProfitReport } from "@/components/ProfitReport";

const now = new Date();
const thisMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
const monthLabel = (m) => new Date(`${m}-01T00:00:00`).toLocaleDateString("en-AU", { month: "short", year: "2-digit" });

const Stat = ({ label, value, testid }) => (
  <div className="rounded-lg border border-slate-200 bg-white p-5">
    <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</div>
    <div className="num mt-2 text-2xl font-semibold text-[#01195D]" data-testid={testid}>{value}</div>
  </div>
);

const Ranking = ({ title, rows, testid }) => (
  <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white" data-testid={testid}>
    <div className="border-b border-slate-200 px-5 py-3 font-semibold text-[#01195D]">{title}</div>
    <table className="w-full text-sm">
      <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
        <tr><th className="w-10 px-4 py-2.5">#</th><th className="px-4 py-2.5">Name</th><th className="px-4 py-2.5 text-right">Quotes</th><th className="px-4 py-2.5 text-right">Quoted</th><th className="px-4 py-2.5 text-right">Orders</th><th className="px-4 py-2.5 text-right">Ordered</th></tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={r.name} className="row-hover border-t border-slate-100">
            <td className="px-4 py-2.5">{i === 0 && r.ordered > 0 ? <Trophy className="h-4 w-4 text-amber-500" /> : <span className="text-slate-400">{i + 1}</span>}</td>
            <td className="px-4 py-2.5 font-medium text-[#0B1523]">{r.name}</td>
            <td className="num px-4 py-2.5 text-right">{r.quotes}</td>
            <td className="num px-4 py-2.5 text-right text-slate-500">{money(r.quoted)}</td>
            <td className="num px-4 py-2.5 text-right">{r.orders}</td>
            <td className="num px-4 py-2.5 text-right font-semibold text-[#01195D]">{money(r.ordered)}</td>
          </tr>
        ))}
        {!rows.length && <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-500">No sales this month.</td></tr>}
      </tbody>
    </table>
  </div>
);

export default function Reports() {
  const [month, setMonth] = useState(thisMonth);
  const [data, setData] = useState(null);
  useEffect(() => { if (month) api.get("/reports/monthly", { params: { month } }).then((r) => setData(r.data)); }, [month]);

  return (
    <div>
      <PageHeader title="Sales reports" subtitle="Monthly performance by rep and customer">
        <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-44" data-testid="reports-month-input" />
      </PageHeader>
      <Tabs defaultValue="sales">
        <TabsList className="mb-6 h-auto max-w-full flex-wrap justify-start">
          <TabsTrigger value="sales" data-testid="reports-tab-sales">Sales performance</TabsTrigger>
          <TabsTrigger value="profit" data-testid="reports-tab-profit">Gross profit</TabsTrigger>
        </TabsList>
        <TabsContent value="profit"><ProfitReport month={month} /></TabsContent>
        <TabsContent value="sales">
      {data && (
        <div className="space-y-6 fade-up">
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <Stat label="Quotes" value={data.totals.quotes} testid="report-quotes" />
            <Stat label="Quoted value" value={money(data.totals.quoted)} testid="report-quoted" />
            <Stat label="Orders" value={data.totals.orders} testid="report-orders" />
            <Stat label="Order value" value={money(data.totals.ordered)} testid="report-ordered" />
          </div>
          <div className="rounded-lg border border-slate-200 bg-white p-5" data-testid="report-trend-chart">
            <div className="mb-4 font-semibold text-[#01195D]">Last 6 months</div>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={data.trend.map((t) => ({ ...t, label: monthLabel(t.month) }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `$${v >= 1000 ? `${Math.round(v / 1000)}k` : v}`} />
                <Tooltip formatter={(v) => money(v)} />
                <Legend />
                <Bar dataKey="quoted" name="Quoted" fill="#1BA3E0" radius={[4, 4, 0, 0]} />
                <Bar dataKey="ordered" name="Ordered" fill="#01195D" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="grid gap-6 lg:grid-cols-2">
            <Ranking title="By sales rep" rows={data.by_rep} testid="report-by-rep" />
            <Ranking title="By customer" rows={data.by_customer} testid="report-by-customer" />
          </div>
        </div>
      )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
